import { type FC, type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { noop, useToggle } from '@direct-frontend/stdlib';
import { INITIAL_MODAL_STEP, type InventoryCompareProductsModalStep, MODAL_STEP } from './lib/steps';
import { type OpenApiSchemaImportTypeSettings, type OpenApiSchemaIntegrationStatus } from '../../api/schema';
import { type SupportedIntegrationTypes, type TagWithScope } from '../../lib/integrations';
import {
    InventoryCompareProductsModalContext,
    type InventoryCompareProductsModalContextType,
} from './InventoryCompareProductsModalContext';

type InventoryCompareProductsModalProviderProps = PropsWithChildren<{}>;

export const InventoryCompareProductsModalProvider: FC<InventoryCompareProductsModalProviderProps> = ({ children }) => {
    const { value: isOpen, toggleOn: open, toggleOff: close } = useToggle(false);
    const { value: isSaving, toggleOn: startSaving, toggleOff: stopSaving } = useToggle(false);
    const {
        value: isParametersReadonly,
        toggleOff: setParametersWritable,
        setValue: setParametersReadonly,
    } = useToggle(false);
    const [isLoading, setLoadingStatus] = useState<boolean>(false);

    const [source, setSource] = useState<InventoryCompareProductsModalContextType['source']>(null);
    const [step, setStep] = useState<InventoryCompareProductsModalStep>(INITIAL_MODAL_STEP);
    const [isProcessing, setIsProcessing] = useState(false);
    const [dryMappingStatus, setDryMappingStatus] = useState<OpenApiSchemaIntegrationStatus | undefined>(undefined);
    const [strategy, setStrategy] = useState<OpenApiSchemaImportTypeSettings['create_strategy']>('create');
    const [parameters, setParameters] = useState<TagWithScope[]>([]);
    const [changeSource, setChangeSource] = useState<InventoryCompareProductsModalContextType['changeSource']>(noop);

    const resetContext = useCallback(() => {
        setSource(null);
        setStep(INITIAL_MODAL_STEP);
        setIsProcessing(false);
        setDryMappingStatus(undefined);
        setStrategy('create');
        setParameters([]);
        setChangeSource(noop);
        setParametersWritable();
        setLoadingStatus(false);
    }, [setIsProcessing, setParametersWritable]);

    const handleOpen = useCallback(
        (source: SupportedIntegrationTypes, changeSource: InventoryCompareProductsModalContextType['changeSource']) => {
            setSource(source);
            setChangeSource(() => changeSource);
            open();
        },
        [open]
    );

    const handleClose = useCallback(() => {
        close();
        resetContext();
    }, [close, resetContext]);

    const goToNextStep = useCallback(() => {
        if (step === MODAL_STEP.mappingSettings) {
            setStep(MODAL_STEP.testMapping);
        } else {
            setStep(MODAL_STEP.mappingSettings);
        }
    }, [step]);

    const goToPreviousStep = useCallback(() => {
        if (step === MODAL_STEP.testMapping) {
            setStep(MODAL_STEP.mappingSettings);
        } else {
            setStep(MODAL_STEP.testMapping);
        }
    }, [step]);

    const handleSetParameters: typeof setParameters = useCallback(
        (parameters) => {
            setParametersReadonly(Array.isArray(parameters) && parameters.length > 0);

            return setParameters(parameters);
        },
        [setParameters, setParametersReadonly]
    );

    const contextValue = useMemo<InventoryCompareProductsModalContextType>(() => {
        return {
            isOpen: isOpen && Boolean(source),
            changeSource,
            open: handleOpen,
            close: handleClose,
            source,
            step,
            goToNextStep,
            goToPreviousStep,
            isLoading: {
                value: isLoading,
                setValue: setLoadingStatus,
            },
            dryMapping: {
                status: dryMappingStatus,
                setStatus: setDryMappingStatus,
            },
            isProcessingDryMapping: {
                value: isProcessing,
                setValue: setIsProcessing,
            },
            strategy: {
                value: strategy,
                setValue: setStrategy,
            },
            parameters: {
                value: parameters,
                setValue: handleSetParameters,
                isReadonly: isParametersReadonly,
            },
            save: {
                isSaving,
                startSaving,
                stopSaving,
            },
        };
    }, [
        changeSource,
        dryMappingStatus,
        goToNextStep,
        goToPreviousStep,
        handleClose,
        handleOpen,
        handleSetParameters,
        isLoading,
        isOpen,
        isParametersReadonly,
        isProcessing,
        isSaving,
        parameters,
        setIsProcessing,
        source,
        startSaving,
        step,
        stopSaving,
        strategy,
    ]);

    return (
        <InventoryCompareProductsModalContext.Provider value={contextValue}>
            {children}
        </InventoryCompareProductsModalContext.Provider>
    );
};
